import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Menu,
  X,
  MessageCircle,
  ArrowRight,
  ArrowUpRight,
  ShieldCheck,
  Zap,
  Layers,
  Flame,
  Settings2,
  Wrench,
  Ruler,
  Boxes,
  CheckCircle2,
  Factory,
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  Linkedin,
  Instagram,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SITE, waLink } from "@/lib/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ---------- Header ---------- */
const NAV_ITEMS = [
  { label: "Início", href: "#inicio" },
  { label: "Soluções", href: "#solucoes" },
  { label: "Benefícios", href: "#beneficios" },
  { label: "FAQ", href: "#faq" },
  { label: "Contato", href: "#contato" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-[0_1px_0_rgba(13,31,59,0.04)]"
          : "bg-white border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-20">
        <a
          href="#inicio"
          data-testid="logo-link"
          className="flex items-center"
          aria-label="Buzas — Soluções Industriais"
        >
          <img
            src={SITE.logos.header}
            alt="Buzas — Soluções Industriais"
            className="h-12 lg:h-14 w-auto object-contain"
            loading="eager"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV_ITEMS.map((it) => (
            <a
              key={it.href}
              href={it.href}
              data-testid={`nav-${it.href.replace("#", "")}`}
              className="text-sm font-semibold text-[#1A2B56] hover:text-[#e63946] transition-colors relative after:absolute after:left-0 after:-bottom-2 after:h-[2px] after:w-0 after:bg-[#e63946] hover:after:w-full after:transition-all"
            >
              {it.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:block">
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-header-cta"
          >
            <Button className="bg-[#e63946] hover:bg-[#c92a36] text-white rounded-sm h-11 px-5 font-semibold">
              <MessageCircle className="h-4 w-4" strokeWidth={2} />
              Falar no WhatsApp
            </Button>
          </a>
        </div>

        <button
          type="button"
          data-testid="mobile-menu-toggle"
          className="lg:hidden inline-flex items-center justify-center h-10 w-10 text-[#1A2B56]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Abrir menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu-panel"
          className="lg:hidden border-t border-slate-200 bg-white"
        >
          <div className="px-4 py-4 flex flex-col gap-1">
            {NAV_ITEMS.map((it) => (
              <a
                key={it.href}
                href={it.href}
                data-testid={`mobile-nav-${it.href.replace("#", "")}`}
                onClick={() => setOpen(false)}
                className="py-3 px-2 text-base font-semibold text-[#1A2B56] border-b border-slate-100 hover:text-[#e63946] flex items-center justify-between"
              >
                {it.label}
                <ArrowRight size={16} />
              </a>
            ))}
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-whatsapp-cta"
              className="mt-3"
            >
              <Button className="bg-[#e63946] hover:bg-[#c92a36] text-white rounded-sm h-12 w-full font-semibold">
                <MessageCircle className="h-4 w-4" /> Falar no WhatsApp
              </Button>
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

/* ---------- Hero ---------- */
const Hero = () => (
  <section
    id="inicio"
    data-testid="hero-section"
    className="relative pt-32 lg:pt-40 pb-20 lg:pb-28 bg-white overflow-hidden"
  >
    <div className="absolute inset-0 tech-grid pointer-events-none opacity-70" />
    <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center relative">
      <div className="lg:col-span-7">
        <div className="inline-flex items-center gap-2 border border-slate-200 bg-white px-3 py-1.5 rounded-sm mb-6 rise">
          <span className="h-1.5 w-1.5 rounded-full bg-[#e63946]" />
          <span className="text-[11px] uppercase tracking-[0.22em] text-[#1A2B56] font-bold">
            +30 anos em metalurgia de precisão
          </span>
        </div>

        <h1
          data-testid="hero-h1"
          className="font-titillium font-black text-[#1A2B56] text-4xl sm:text-5xl lg:text-[64px] leading-[1.05] tracking-tight rise delay-1"
        >
          Equipamentos{" "}
          <span className="relative inline-block">
            industriais
            <span className="absolute -bottom-1 left-0 right-0 h-[6px] bg-[#e63946]/90" />
          </span>{" "}
          sob medida com precisão técnica
        </h1>

        <p
          data-testid="hero-headline"
          className="mt-6 text-lg lg:text-xl text-[#1A2B56] font-semibold max-w-xl rise delay-2"
        >
          Projetos de estruturas metálicas que fazem parte da sua máquina.
        </p>

        <p className="mt-4 text-base text-slate-600 max-w-xl leading-relaxed rise delay-3">
          Fabricação industrial sob medida para pequenas e médias indústrias,
          com precisão técnica, resistência estrutural e confiabilidade
          operacional.
        </p>

        <p className="mt-3 text-sm text-slate-500 max-w-xl rise delay-3">
          Da engenharia à entrega final, transformamos projetos em soluções
          industriais eficientes.
        </p>

        <div className="mt-8 flex flex-col sm:flex-row gap-3 rise delay-4">
          <a href="#contato" data-testid="hero-primary-cta">
            <Button className="bg-[#e63946] hover:bg-[#c92a36] text-white rounded-sm h-12 px-6 text-sm font-bold w-full sm:w-auto">
              Solicitar orçamento{" "}
              <ArrowRight className="h-4 w-4 cta-arrow" strokeWidth={2.5} />
            </Button>
          </a>
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-cta"
          >
            <Button
              variant="outline"
              className="border-2 border-[#1A2B56] text-[#1A2B56] hover:bg-[#1A2B56] hover:text-white rounded-sm h-12 px-6 text-sm font-bold w-full sm:w-auto transition-colors"
            >
              <MessageCircle className="h-4 w-4" />
              Falar no WhatsApp
            </Button>
          </a>
        </div>

        <p className="mt-4 text-xs text-slate-500 flex items-center gap-2 rise delay-4">
          <Send size={13} strokeWidth={1.5} />
          Envie seu projeto e receba orientação técnica.
        </p>

        {/* Trust strip */}
        <div className="mt-10 grid grid-cols-3 gap-4 max-w-xl rise delay-4">
          {[
            { k: "30+", v: "anos de mercado" },
            { k: "100%", v: "produção integrada" },
            { k: "B2B", v: "indústria alimentícia e têxtil" },
          ].map((t) => (
            <div
              key={t.k}
              className="border-l-2 border-[#e63946] pl-3"
              data-testid={`hero-trust-${t.k}`}
            >
              <div className="font-titillium font-black text-2xl text-[#1A2B56] leading-none">
                {t.k}
              </div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-1 font-semibold">
                {t.v}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Hero image */}
      <div className="lg:col-span-5 rise delay-2">
        <div className="hero-image-frame rounded-sm overflow-hidden aspect-[4/5] bg-slate-900">
          <img
            src={SITE.images.heroMacro}
            alt="Equipamento industrial em aço - macro de componente metálico"
            className="h-full w-full object-cover"
            loading="eager"
          />
        </div>
        <div className="mt-4 flex items-center justify-between text-xs text-slate-500 uppercase tracking-widest font-semibold">
          <span>Aço industrial</span>
          <span className="h-px flex-1 bg-slate-200 mx-3" />
          <span>Precisão ±0,1mm</span>
        </div>
      </div>
    </div>
  </section>
);

/* ---------- Problem / Solution ---------- */
const ProblemSolution = () => (
  <section
    id="solucoes"
    data-testid="problem-solution-section"
    className="bg-[#f8fafc] py-20 lg:py-28 border-y border-slate-200"
  >
    <div className="max-w-7xl mx-auto px-4 md:px-8">
      <div className="max-w-3xl mb-12 lg:mb-16">
        <p className="accent-bar text-[11px] uppercase tracking-[0.22em] text-[#e63946] font-bold">
          Soluções Industriais
        </p>
        <h2 className="mt-3 font-titillium font-black text-[#1A2B56] text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
          Soluções industriais que movem a indústria de máquinas e equipamentos.
        </h2>
        <p className="mt-5 text-base text-slate-600 leading-relaxed">
          A Buzas desenvolve e fabrica componentes metálicos sob medida para
          fabricantes de máquinas e equipamentos industriais, entregando
          precisão, resistência e alto padrão de acabamento em cada projeto.
        </p>
      </div>

      <div className="grid grid-cols-1">
        {/* Solution */}
        <article
          data-testid="solution-card"
          className="bg-[#1A2B56] text-white p-8 lg:p-10 rounded-sm relative overflow-hidden grain-overlay"
        >
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#e63946] font-bold mb-5">
            <ShieldCheck size={14} strokeWidth={2} />
            A Solução Buzas
          </div>
          <h3 className="font-titillium font-bold text-2xl text-white mb-4 leading-tight">
            Da engenharia à entrega final, projetos em soluções eficientes.
          </h3>
          <p className="text-slate-300 leading-relaxed">
            Atuamos lado a lado com nossos clientes no desenvolvimento de peças,
            estruturas e componentes que garantem desempenho, produtividade e
            confiabilidade para operações industriais.
          </p>

          <ul className="mt-6 space-y-3">
            {[
              "Corte a laser de alta precisão",
              "Dobra CNC, usinagem e soldagem técnica",
              "Caldeiraria leve e montagem de conjuntos",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm">
                <CheckCircle2
                  size={18}
                  className="text-[#e63946] mt-0.5 shrink-0"
                  strokeWidth={2}
                />
                <span className="text-slate-200 font-medium">{t}</span>
              </li>
            ))}
          </ul>
        </article>
      </div>
    </div>
  </section>
);

/* ---------- Technical Benefits ---------- */
const BENEFITS = [
  {
    icon: Zap,
    title: "Corte a laser preciso",
    desc: "Acabamento limpo e alta tolerância dimensional para peças com exigência técnica.",
  },
  {
    icon: Layers,
    title: "Dobra CNC padronizada",
    desc: "Repetibilidade e consistência em séries longas, reduzindo variações de processo.",
  },
  {
    icon: Boxes,
    title: "Caldeiraria leve resistente",
    desc: "Estruturas robustas dimensionadas para operações industriais de alta exigência.",
  },
  {
    icon: Flame,
    title: "Soldagem industrial robusta",
    desc: "Processos qualificados para maior durabilidade estrutural e confiabilidade.",
  },
  {
    icon: Settings2,
    title: "Usinagem sob medida",
    desc: "Componentes técnicos fabricados conforme projeto, desenho ou amostra.",
  },
  {
    icon: Wrench,
    title: "Montagem final integrada",
    desc: "Corte, dobra, solda e usinagem em fluxo único — prazo e qualidade sob controle.",
  },
  {
    icon: Ruler,
    title: "Controle dimensional rigoroso",
    desc: "Inspeção e verificação em cada etapa para garantir padrão consistente entre lotes.",
  },
];

const TechnicalBenefits = () => (
  <section
    id="beneficios"
    data-testid="benefits-section"
    className="bg-white py-20 lg:py-28"
  >
    <div className="max-w-7xl mx-auto px-4 md:px-8">
      <div className="max-w-3xl mb-12 lg:mb-16">
        <p className="accent-bar text-[11px] uppercase tracking-[0.22em] text-[#e63946] font-bold">
          Benefícios Técnicos
        </p>
        <h2 className="mt-3 font-titillium font-black text-[#1A2B56] text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
          Um parque fabril integrado para uma operação mais eficiente.
        </h2>
        <p className="mt-5 text-base text-slate-600 leading-relaxed">
          Todos os processos acontecem internamente na Buzas — do corte a laser
          à montagem final. Isso reduz falhas, encurta prazos e garante padrão
          técnico consistente em cada peça entregue.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {BENEFITS.map((b, i) => {
          const Icon = b.icon;
          const span = i === 0 ? "lg:col-span-2" : "";
          return (
            <article
              key={b.title}
              data-testid={`benefit-card-${i + 1}`}
              className={`benefit-card bg-white border border-slate-200 p-6 lg:p-7 rounded-sm group ${span}`}
            >
              <div className="flex items-start justify-between mb-8">
                <div className="h-11 w-11 rounded-sm border border-slate-200 bg-[#f8fafc] flex items-center justify-center group-hover:border-[#1A2B56] transition-colors">
                  <Icon
                    size={20}
                    strokeWidth={1.5}
                    className="text-[#1A2B56]"
                  />
                </div>
                <span className="text-[11px] font-mono tracking-widest text-slate-400">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <h3 className="font-titillium font-bold text-[#1A2B56] text-lg leading-snug mb-2">
                {b.title}
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                {b.desc}
              </p>
            </article>
          );
        })}
      </div>
    </div>
  </section>
);

/* ---------- Authority / 30 years ---------- */
const Authority = () => (
  <section
    data-testid="authority-section"
    className="bg-white py-20 lg:py-32"
  >
    <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
      {/* Left — content */}
      <div className="lg:col-span-6 order-2 lg:order-1">
        <p
          data-testid="authority-overline"
          className="font-titillium font-bold text-[13px] tracking-[0.22em] uppercase text-[#1A2B56]"
        >
          Prova Social
        </p>

        <h2
          data-testid="authority-title"
          className="mt-5 font-titillium font-bold text-[#1A2B56] text-3xl sm:text-4xl lg:text-5xl leading-[1.15] tracking-tight"
        >
          Com mais de{" "}
          <span className="font-black text-[#e63946]">30 anos</span> na
          indústria metalúrgica.
        </h2>

        <p
          data-testid="authority-paragraph"
          className="mt-7 font-inter text-base lg:text-lg text-[#1A2B56]/90 leading-relaxed max-w-xl"
        >
          A Buzas desenvolve estruturas e componentes para operações que exigem
          estabilidade, padronização e desempenho contínuo.
        </p>

        <p className="mt-4 font-inter text-base lg:text-lg text-[#1A2B56]/90 leading-relaxed max-w-xl">
          Nossa produção integrada reduz retrabalho, melhora a confiabilidade
          das peças e aumenta a previsibilidade da sua operação.
        </p>

        <div className="mt-10 flex flex-wrap items-center gap-8 border-t border-slate-200 pt-8 max-w-xl">
          <div>
            <div className="font-titillium font-bold text-4xl text-[#1A2B56] leading-none">
              30+
            </div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#1A2B56]/70 mt-2 font-semibold">
              Anos de indústria
            </div>
          </div>
          <div className="h-10 w-px bg-slate-200" />
          <div>
            <div className="font-titillium font-bold text-4xl text-[#1A2B56] leading-none">
              100%
            </div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#1A2B56]/70 mt-2 font-semibold">
              Produção integrada
            </div>
          </div>
        </div>
      </div>

      {/* Right — image */}
      <div className="lg:col-span-6 order-1 lg:order-2">
        <div className="relative aspect-[4/5] w-full overflow-hidden rounded-sm border border-slate-200">
          <img
            src={SITE.images.factoryFloor}
            alt="Linha de produção metalúrgica — Buzas"
            className="absolute inset-0 h-full w-full object-cover"
            loading="lazy"
          />
        </div>
      </div>
    </div>
  </section>
);

/* ---------- Main CTA + Form ---------- */
const MainCTA = () => {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Preencha nome, e-mail e telefone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success(
        "Orçamento recebido! Nossa equipe técnica entrará em contato em breve.",
      );
      setForm({ name: "", company: "", email: "", phone: "", message: "" });
    } catch (err) {
      const raw =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "";
      const msg = String(raw).toLowerCase().includes("email")
        ? "E-mail inválido. Verifique e tente novamente."
        : raw
          ? "Não foi possível enviar. Verifique os dados e tente novamente."
          : "Falha de conexão. Tente novamente em instantes.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contato"
      data-testid="main-cta-section"
      className="bg-[#1A2B56] text-white py-20 lg:py-28 relative overflow-hidden grain-overlay"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start relative">
        <div className="lg:col-span-5">
          <p className="accent-bar text-[11px] uppercase tracking-[0.22em] text-[#e63946] font-bold">
            Fale com a Buzas
          </p>
          <h2
            data-testid="main-cta-title"
            className="mt-3 font-titillium font-black text-3xl sm:text-4xl lg:text-[46px] tracking-tight leading-[1.05]"
          >
            Solicite orçamento técnico para sua indústria.
          </h2>
          <p className="mt-5 text-slate-300 text-lg leading-relaxed">
            Fale com um especialista agora.
          </p>
          <p className="mt-3 text-slate-400 text-sm leading-relaxed max-w-md">
            Envie desenho técnico ou descrição da demanda para análise.
            Retornamos com orientação técnica e prazo estimado.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-whatsapp-button"
            >
              <Button className="bg-[#e63946] hover:bg-[#c92a36] text-white rounded-sm h-12 px-6 text-sm font-bold w-full sm:w-auto">
                <MessageCircle className="h-4 w-4" />
                Falar no WhatsApp
              </Button>
            </a>
            <a href="#form-orcamento" data-testid="cta-scroll-form">
              <Button
                variant="outline"
                className="border-2 border-white bg-transparent text-white hover:bg-white hover:text-[#1A2B56] rounded-sm h-12 px-6 text-sm font-bold w-full sm:w-auto transition-colors"
              >
                Solicitar orçamento
              </Button>
            </a>
          </div>

          <ul className="mt-10 space-y-3">
            {[
              "Retorno técnico rápido",
              "Atendimento a PMEs e indústrias",
              "Projetos sob medida com suporte contínuo",
            ].map((t) => (
              <li key={t} className="flex items-center gap-3 text-slate-300">
                <CheckCircle2
                  size={18}
                  className="text-[#e63946] shrink-0"
                  strokeWidth={2}
                />
                <span className="text-sm font-medium">{t}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-7" id="form-orcamento">
          <form
            onSubmit={submit}
            data-testid="quote-form"
            className="bg-white text-[#1A2B56] p-6 lg:p-8 rounded-sm shadow-[0_20px_60px_rgba(0,0,0,0.25)] border border-white/10"
          >
            <div className="flex items-center justify-between mb-6 border-b border-slate-200 pb-4">
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#e63946] font-bold">
                <Send size={13} strokeWidth={2} />
                Orçamento Técnico
              </div>
              <span className="text-xs text-slate-500 font-semibold">
                Resposta em até 24h úteis
              </span>
            </div>
            <h3 className="font-titillium font-bold text-xl mb-5">
              Envie os dados do seu projeto
            </h3>

            <div className="space-y-4">
              <div>
                <label className="text-[11px] uppercase tracking-widest text-slate-600 font-bold">
                  Nome *
                </label>
                <Input
                  required
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Seu nome completo"
                  data-testid="form-input-name"
                  className="mt-2 h-11 rounded-sm border-slate-300 focus-visible:ring-[#1A2B56]"
                />
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-widest text-slate-600 font-bold">
                  Empresa
                </label>
                <Input
                  value={form.company}
                  onChange={update("company")}
                  placeholder="Nome da empresa (opcional)"
                  data-testid="form-input-company"
                  className="mt-2 h-11 rounded-sm border-slate-300 focus-visible:ring-[#1A2B56]"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] uppercase tracking-widest text-slate-600 font-bold">
                    E-mail *
                  </label>
                  <Input
                    required
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="voce@empresa.com.br"
                    data-testid="form-input-email"
                    className="mt-2 h-11 rounded-sm border-slate-300 focus-visible:ring-[#1A2B56]"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-widest text-slate-600 font-bold">
                    Telefone / WhatsApp *
                  </label>
                  <Input
                    required
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="(00) 00000-0000"
                    data-testid="form-input-phone"
                    className="mt-2 h-11 rounded-sm border-slate-300 focus-visible:ring-[#1A2B56]"
                  />
                </div>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-widest text-slate-600 font-bold">
                  Mensagem
                </label>
                <Textarea
                  rows={4}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Conte brevemente sobre seu projeto, peça ou demanda técnica."
                  data-testid="form-input-message"
                  className="mt-2 rounded-sm border-slate-300 focus-visible:ring-[#1A2B56]"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="form-submit-button"
                className="bg-[#e63946] hover:bg-[#c92a36] text-white rounded-sm h-12 w-full font-bold text-sm"
              >
                {loading ? "Enviando..." : "Solicitar orçamento"}
                {!loading && (
                  <ArrowRight className="h-4 w-4 cta-arrow" strokeWidth={2.5} />
                )}
              </Button>

              <p className="text-[11px] text-slate-500 text-center leading-relaxed pt-1">
                Ao enviar, você concorda em ser contatado pela equipe técnica
                da Buzas, conforme a nossa{" "}
                <Link
                  to={SITE.privacyPath}
                  data-testid="form-privacy-link"
                  className="underline underline-offset-2 hover:text-[#1A2B56] transition-colors"
                >
                  Política de Privacidade
                </Link>
                .
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

/* ---------- FAQ ---------- */
const FAQS = [
  {
    q: "Vocês fabricam equipamentos completos?",
    a: "Produzimos estruturas e componentes sob projeto, incluindo montagem final.",
  },
  {
    q: "Atendem pequenas e médias indústrias?",
    a: "Sim. Desenvolvemos soluções conforme a necessidade de cada operação.",
  },
  {
    q: "Trabalham com produção recorrente?",
    a: "Sim. Oferecemos fornecimento contínuo quando necessário.",
  },
  {
    q: "Como solicitar orçamento?",
    a: "Envie desenho técnico ou descrição da demanda para análise.",
  },
];

const FAQ = () => (
  <section
    id="faq"
    data-testid="faq-section"
    className="bg-[#f8fafc] py-20 lg:py-28"
  >
    <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
      <div className="lg:col-span-5">
        <p className="accent-bar text-[11px] uppercase tracking-[0.22em] text-[#e63946] font-bold">
          Perguntas Frequentes
        </p>
        <h2 className="mt-3 font-titillium font-black text-[#1A2B56] text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
          Dúvidas reais de quem produz.
        </h2>
        <p className="mt-5 text-base text-slate-600 leading-relaxed max-w-md">
          Respostas objetivas para você avaliar com clareza a aplicação dos
          nossos processos na sua operação.
        </p>
        <a
          href={waLink()}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="faq-whatsapp-link"
          className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-[#e63946] hover:text-[#c92a36] group"
        >
          Não encontrou sua dúvida? Fale com um especialista
          <ArrowUpRight
            size={16}
            strokeWidth={2.5}
            className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
          />
        </a>
      </div>

      <div className="lg:col-span-7">
        <Accordion
          type="single"
          collapsible
          className="bg-white border border-slate-200 rounded-sm"
          defaultValue="item-0"
          data-testid="faq-accordion"
        >
          {FAQS.map((f, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              className={`border-slate-200 ${i === FAQS.length - 1 ? "border-b-0" : ""}`}
            >
              <AccordionTrigger
                data-testid={`faq-trigger-${i}`}
                className="px-6 py-5 text-base font-bold text-[#1A2B56] hover:no-underline hover:text-[#e63946] [&[data-state=open]]:text-[#1A2B56] [&>svg]:h-5 [&>svg]:w-5"
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent
                data-testid={`faq-content-${i}`}
                className="px-6 pb-5 text-slate-600 leading-relaxed text-sm"
              >
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  </section>
);

/* ---------- Footer ---------- */
const Footer = () => (
  <footer
    data-testid="site-footer"
    className="bg-[#1A2B56] text-slate-300 pt-16 pb-10 grain-overlay relative"
  >
    <div className="max-w-7xl mx-auto px-4 md:px-8">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <img
            src={SITE.logos.footer}
            alt="Buzas"
            className="h-16 w-auto object-contain mb-6"
            style={{ mixBlendMode: "screen" }}
          />
          <p className="text-sm text-slate-400 leading-relaxed max-w-sm">
            Componentes metálicos sob medida para fabricantes de máquinas e
            equipamentos industriais. Corte a laser, dobra CNC, soldagem,
            usinagem e montagem com precisão técnica.
          </p>
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-whatsapp-cta"
            className="mt-6 inline-flex"
          >
            <Button className="bg-[#e63946] hover:bg-[#c92a36] text-white rounded-sm h-11 px-5 font-bold text-sm">
              <MessageCircle className="h-4 w-4" />
              Falar no WhatsApp
            </Button>
          </a>
        </div>

        <div className="md:col-span-3">
          <div className="text-[11px] uppercase tracking-[0.22em] text-slate-400 font-bold mb-4">
            Navegação
          </div>
          <ul className="space-y-3">
            {NAV_ITEMS.filter((i) => i.href !== "#beneficios").map((i) => (
              <li key={i.href}>
                <a
                  href={i.href}
                  data-testid={`footer-nav-${i.href.replace("#", "")}`}
                  className="text-sm text-slate-300 hover:text-white transition-colors"
                >
                  {i.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="text-[11px] uppercase tracking-[0.22em] text-slate-400 font-bold mb-4">
            Contato
          </div>
          <ul className="space-y-3 text-sm">
            <li className="flex items-center gap-3 text-slate-300">
              <Phone size={14} strokeWidth={1.8} className="text-[#e63946]" />
              <span data-testid="footer-phone">{SITE.phoneDisplay}</span>
            </li>
            <li className="flex items-center gap-3 text-slate-300">
              <Mail size={14} strokeWidth={1.8} className="text-[#e63946]" />
              <a
                href={`mailto:${SITE.email}`}
                data-testid="footer-email"
                className="hover:text-white transition-colors"
              >
                {SITE.email}
              </a>
            </li>
            <li className="flex items-center gap-3 text-slate-300">
              <Linkedin size={14} strokeWidth={1.8} className="text-[#e63946]" />
              <a
                href={SITE.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-linkedin"
                className="hover:text-white transition-colors"
              >
                LinkedIn
              </a>
            </li>
            <li className="flex items-center gap-3 text-slate-300">
              <Instagram
                size={14}
                strokeWidth={1.8}
                className="text-[#e63946]"
              />
              <a
                href={SITE.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="hover:text-white transition-colors"
              >
                {SITE.instagramHandle}
              </a>
            </li>
            <li className="flex items-center gap-3 text-slate-300">
              <MapPin size={14} strokeWidth={1.8} className="text-[#e63946]" />
              <span>{SITE.location}</span>
            </li>
            <li className="flex items-center gap-3 text-slate-300">
              <Clock size={14} strokeWidth={1.8} className="text-[#e63946]" />
              <span>Seg — Sex · 08h às 18h</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
        <p className="text-xs text-slate-400">
          © {new Date().getFullYear()} Buzas.{" "}
          <Link
            to={SITE.privacyPath}
            data-testid="footer-privacy-link"
            className="underline underline-offset-4 hover:text-white transition-colors"
          >
            Política de Privacidade
          </Link>
        </p>
        <p className="text-xs text-slate-400 uppercase tracking-[0.22em] font-semibold">
          Equipamentos Industriais — Metalurgia de Precisão
        </p>
      </div>
    </div>
  </footer>
);

/* ---------- Landing ---------- */
export default function Landing() {
  // Sanity ping to backend
  useEffect(() => {
    axios.get(`${API}/`).catch(() => {});
  }, []);

  return (
    <main className="bg-white text-[#1A2B56]">
      <Header />
      <Hero />
      <ProblemSolution />
      <TechnicalBenefits />
      <Authority />
      <MainCTA />
      <FAQ />
      <Footer />
    </main>
  );
}
